import React, { useEffect, useMemo, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { html as htmlLanguage } from '@codemirror/lang-html';
import { css as cssLanguage } from '@codemirror/lang-css';
import { Decoration, EditorView } from '@codemirror/view';
import { StateEffect, StateField } from '@codemirror/state';
import { oneDark } from '@codemirror/theme-one-dark';

// The validator answers in line numbers, so the editor has to be able to point at a line.
// Without this the operator counts lines with a finger on the screen, which is what makes a
// correct gate unusable in practice.
const setErrorLines = StateEffect.define();

const errorLineDecoration = Decoration.line({ class: 'result-template-error-line' });

const errorLineField = StateField.define({
    create: () => Decoration.none,
    update(decorations, transaction) {
        let next = decorations.map(transaction.changes);

        for (const effect of transaction.effects) {
            if (!effect.is(setErrorLines)) continue;

            const ranges = effect.value
                .filter((line) => Number.isInteger(line) && line >= 1 && line <= transaction.state.doc.lines)
                .map((line) => errorLineDecoration.range(transaction.state.doc.line(line).from));

            next = Decoration.set(ranges, true);
        }

        return next;
    },
    provide: (field) => EditorView.decorations.from(field),
});

// The dashboard renders on --gray-900, so the editor is dark too.
//
// oneDark has to arrive through the `theme` prop, not through `extensions`. @uiw's wrapper
// defaults `theme` to 'light' and then appends its own
// EditorView.theme({ '&': { backgroundColor: '#fff' } }) to the extension list *ahead* of
// the ones passed in, which outranks a oneDark added here -- the syntax colours take, the
// white background stays, and you get one-dark's bright palette on white. Setting the prop
// is what stops that light default from being added at all.
const editorTheme = EditorView.theme(
    {
        '&': { fontSize: '13px' },
        '.cm-scroller': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
        '.result-template-error-line': { backgroundColor: 'rgba(241, 37, 49, 0.22)' },
    },
    { dark: true },
);

export default function ResultTemplateCodeEditor({
    language,
    value,
    onChange,
    errorLines = [],
    readOnly = false,
    height = '420px',
}) {
    const viewRef = useRef(null);
    const extensions = useMemo(
        () => [
            language === 'css' ? cssLanguage() : htmlLanguage(),
            errorLineField,
            editorTheme,
            EditorView.lineWrapping,
        ],
        [language],
    );

    // Dispatched from an effect rather than an update listener: CodeMirror forbids
    // dispatching inside its own update cycle. The field maps its ranges across every edit,
    // so a marked line stays marked while the operator types until the next report.
    const markedLines = errorLines.join(',');
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({ effects: setErrorLines.of(errorLines) });
        // errorLines is a fresh array each render; the joined key is what actually changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markedLines]);

    return (
        <CodeMirror
            value={value}
            theme={oneDark}
            height={height}
            extensions={extensions}
            editable={!readOnly}
            onChange={onChange}
            onCreateEditor={(view) => {
                viewRef.current = view;
                if (errorLines.length > 0) {
                    view.dispatch({ effects: setErrorLines.of(errorLines) });
                }
            }}
        />
    );
}
